import axios from '../utils/axios';

const apiPrefix = '/api/job-board';

const updateJobBoardProfileSettings = async (data) => {
	const result = await axios.post(`${apiPrefix}/settings`, data);

	return result.data.data;
};

const findJobsJobBoard = async (filters) => {
	const result = await axios.post(`${apiPrefix}/find-jobs`, filters);

	return result.data;
};

const saveJob = async (job) => {
	const result = await axios.post(`${apiPrefix}/save-job`, job);

	return result.data;
};

const getJobs = async () => {
	const result = await axios.get(`${apiPrefix}/jobs`);

	return result.data;
};

const deleteJob = async (jobId) => {
	await axios.delete(`${apiPrefix}/${jobId}`);
};

const updateAppliedStatus = async (jobId, status, queryId = null) => {
	await axios.post(`${apiPrefix}/${jobId}/status`, { applied: status, query_id: queryId });
};

const markJobApplied = async (providerJobId, queryId = null) => {
	await axios.post(`${apiPrefix}/mark-applied`, { provider_job_id: providerJobId, query_id: queryId });
};

/**
 * Record the seeker confirming they submitted an external application (EN-621).
 * Marks the job applied and adds it to Job Tracker in one call.
 */
const confirmApplication = async (providerJobId, queryId = null) => {
	await axios.post(`${apiPrefix}/apply-confirmations`, { provider_job_id: providerJobId, query_id: queryId });
};

const hideJob = async (providerJobId) => {
	await axios.post(`${apiPrefix}/hide-job`, { provider_job_id: providerJobId });
};

/**
 * The one recommended Tier 1 job the Your Journey card shows, and how many stand behind it (EN-1040).
 * `after` is a job the seeker moved past with Hide; the answer is the next one ranked below it.
 */
const getTopRecommendation = async (after = null) => {
	const result = await axios.get(`${apiPrefix}/recommendations/top`, { params: after === null ? {} : { after } });

	return result.data;
};

const trackActivity = async (providerJobId, eventType, queryId = null, position = null) => {
	try {
		await axios.post(`${apiPrefix}/activity/track`, {
			provider_job_id: providerJobId,
			event_type: eventType,
			query_id: queryId,
			position: position
		});
	} catch {
		return null;
	}
};

const giveIndividualFeedback = async (data) => {
	const results =  await axios.post(`${apiPrefix}/feedback`, data);
	return results.data.data;
};

const deleteIndividualFeedback = async (uuid) => {
	await axios.delete(`${apiPrefix}/feedback/${uuid}`);
};

const updateFeedbackComment = async (uuid, data) => {
	await axios.put(`${apiPrefix}/feedback/${uuid}`, data);
};

const fetchJobById = async (jobId) => {
	try {
		const response = await axios.get(`${apiPrefix}/job/${jobId}`);
		return response?.data?.data ?? null;
	} catch (error) {
		console.error('Error fetching job details:', error);
		return null; 
	}
};

const getRecommendedJobs = async (params = {}) => {
	const results = await axios.get(`${apiPrefix}/recommendations`, { params });

	return results?.data;
};

// Job Alerts Api

/** Create a job alert and return it. */
const saveFilters = async (data) => {
	const results =  await axios.post(`${apiPrefix}/subscriptions`, data);
	return results.data.data;
};

const getFilters = async (data) => {
	const results =  await axios.get(`${apiPrefix}/subscriptions`, data);
	return results.data.data;
};

const updateFilter = async (id, data) => {
	const results =  await axios.put(`${apiPrefix}/subscriptions/${id}`, data);
	return results.data;
};

const deleteFilter = async (id) => {
	await axios.delete(`${apiPrefix}/subscriptions/${id}`);
};

const readCookie = (name) => {
	const match = document.cookie
		.split('; ')
		.find((cookie) => cookie.startsWith(`${name}=`));

	return match ? decodeURIComponent(match.slice(name.length + 1)) : '';
};

/**
 * Delete a job alert while the page unloads, on a keepalive request the browser lets finish. An axios request
 * started then dies with the page. Fire and forget: there is no page left to tell.
 */
const deleteFilterOnUnload = (id) => {
	fetch(`${apiPrefix}/subscriptions/${id}`, {
		method: 'DELETE',
		keepalive: true,
		credentials: 'include',
		headers: {
			Accept: 'application/json',
			'X-Requested-With': 'XMLHttpRequest',
			'X-XSRF-TOKEN': readCookie('XSRF-TOKEN'),
		},
	}).catch(() => {});
};

const getPersonalizedJobRecommendations = async () => {
	const results =  await axios.get(`${apiPrefix}/user-settings`);
	return results.data.data;
};

const updatePersonalizedJobRecommendations = async (data) => {
	const results =  await axios.post(`${apiPrefix}/user-settings`, data);
	return results.data.data;
};

const getJobSummary = async (jobId) => {
	const results =  await axios.get(`${apiPrefix}/${jobId}/get-job-summary`);
	return results.data.data;
};

/**
 * The anonymous default algorithm and whether the criteria surface is live for this caller.
 *
 * Public: a signed-out visitor has no `features` map — that ships only on the user resource —
 * so this is the only way the board can know the criteria panel should render.
 */
const getDefaultCriteria = async () => {
	const results = await axios.get(`${apiPrefix}/criteria`);
	return results.data.data;
};

/**
 * The seeker's own saved algorithm, seeded with the default when they have never saved one.
 * `is_default` on the response is how the caller tells the two apart.
 */
const getSavedCriteria = async () => {
	const results = await axios.get(`${apiPrefix}/settings/criteria`);
	return results.data.data;
};

/**
 * The algorithm we would build for this seeker from their parsed resume and onboarding
 * answers — the seed, deliberately ignoring anything they have already saved.
 *
 * Read-only: the seeker adjusts what comes back and `saveCriteria` writes what they settle
 * on. `has_resume` separates "nothing to build from" from "built, but the resume was thin".
 */
const getRecommendedCriteria = async () => {
	const results = await axios.get(`${apiPrefix}/settings/criteria/recommended`);
	return results.data.data;
};

/**
 * Persist the seeker's algorithm. Answers with the stored set, renumbered to a contiguous
 * ranked stack by the server.
 */
const saveCriteria = async (payload) => {
	const results = await axios.put(`${apiPrefix}/settings/criteria`, payload);
	return results.data.data;
};

export default {
	updateJobBoardProfileSettings,
	getDefaultCriteria,
	getSavedCriteria,
	getRecommendedCriteria,
	saveCriteria,
	findJobsJobBoard,
	saveJob,
	getJobs,
	deleteJob,
	updateAppliedStatus,
	markJobApplied,
	confirmApplication,
	hideJob,
	getTopRecommendation,
	trackActivity,
	giveIndividualFeedback,
	deleteIndividualFeedback,
	updateFeedbackComment,
	fetchJobById,
	getRecommendedJobs,
	saveFilters,
	getFilters,
	updateFilter,
	deleteFilter,
	deleteFilterOnUnload,
	getPersonalizedJobRecommendations,
	updatePersonalizedJobRecommendations,
	getJobSummary,
};
